import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, first, map, Observable, of, Subscription } from 'rxjs';
import { ChartConfiguration } from 'src/app/pump-simulation/chartConfiguration';
import { createConfiguration, plotData } from 'src/app/shared/util';
import { ShareDataService } from '../../share-data.service';
import { CalculateCoefficientState } from '../../store/calculate-coefficient/calculate-coefficient.reducer';
import { selectCalculateCoefficient } from '../../store/calculate-coefficient/calculate-coefficient.selector';
import { IntermediateState } from '../../store/charts/intermediate-chart/intermediate-chart.reducer';
import { selectIntermediate } from '../../store/charts/intermediate-chart/intermediate-chart.selector';

declare const google: any;

const AR_CHART_ID = 'arChart';

@Component({
    selector: 'app-ar-chart',
    templateUrl: './ar-chart.component.html',
    styleUrls: ['./ar-chart.component.scss'],
    standalone: false
})
export class ArChartComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];
  xAxisToYaxisChart = null;
  showPlaceholder$: Observable<Boolean>;
  @ViewChild(AR_CHART_ID, { static: true }) chartElement: ElementRef;

  constructor(
    private store: Store,
    private shareService: ShareDataService,
  ) { }

  ngOnInit() {
    const chartLoading = this.shareService.getChartItem().subscribe(data => {
      if (data.includes('mar_') || data.includes('nar_')) {
        this.showPlaceholder$ = of(true);
      }
    });
    this.subscriptions.push(chartLoading);

    const arChart = this.store.pipe(
      select(selectIntermediate),
      map((data: IntermediateState) => data.intermediateData),
      distinctUntilChanged()
    ).subscribe(data => {
      if (data) {
        this.handleARGraph();
      }
    });
    this.subscriptions.push(arChart);
  }

  handleARGraph() {
    const data = this.store.pipe(
      select(selectIntermediate),
      map((data: IntermediateState) => data.chartItems),
      first()
    ).subscribe(data => {
      if (data.includes('arChart')) {
        this.showPlaceholder$ = of(true);
        this.createARGraph();
      }
    });
    this.subscriptions.push(data);
  }

  createARGraph() {

    const arChartConfiguration = createConfiguration({ yAxis: 'AR [h²/(min m^6)]' });

    const arGraph= combineLatest([
      this.store.pipe(
        select(selectCalculateCoefficient),
        map((data: CalculateCoefficientState) => data)
      ),
      this.store.pipe(
        select(selectIntermediate),
        map((data: IntermediateState) => data),
      ),
    ]).pipe(first()).subscribe(([measuredData, calculatedData]) => {

      const arMeasuredData = new Map<number, number>();
      const arCalculatedData = new Map<number, number>();
      const arMeasuredDataMap = new Map();
      const arCalculatedDataMap = new Map();

      for (const entry of measuredData.allCoefficientsData.intermediateCoefficents.entries()) {
        arMeasuredData.set(entry[0], entry[1].ar);
        arMeasuredDataMap.set(1, arMeasuredData);
      }

      if (calculatedData.intermediateData) {

        for (const entry of calculatedData.intermediateData.entries()) {
          arCalculatedData.set(entry[0], entry[1].ar);
          arCalculatedDataMap.set(-1, arCalculatedData);
        }
        this.showPlaceholder$ = of(false);
        google.charts.load('current', { packages: ['corechart'] });
        google.charts.setOnLoadCallback(this.drawChart(arChartConfiguration, this.chartElement.nativeElement, arCalculatedDataMap, arMeasuredDataMap));
      }
    });
    this.subscriptions.push(arGraph);
  }

  drawChart(
    chartConfiguration: ChartConfiguration,
    chart: ElementRef,
    calculation: Map<number, Map<number, number>>,
    measurements: Map<number, Map<number, number>>
  ) {
    return () => {
      let columnIndex = 1;
      let rowIndex = 0;
      const colors = chartConfiguration.colours;
      const data = new google.visualization.DataTable();
      data.addColumn('number', chartConfiguration.xAxisTitle);
      const options = chartConfiguration.options;

      let head;
      ({ head, rowIndex, columnIndex } = plotData(
        calculation, data, rowIndex, chartConfiguration,
        columnIndex, options, colors, 'calculated'));
      ({ head, rowIndex, columnIndex } = plotData(
        measurements, data, rowIndex, chartConfiguration,
        columnIndex, options, colors, 'measured'));

      this.xAxisToYaxisChart = new google.visualization.LineChart(chart);
      google.visualization.events.addListener(this.xAxisToYaxisChart, 'ready', () => {
        // remove loading
      });
      this.xAxisToYaxisChart.draw(data, options);
    };
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    if(this.xAxisToYaxisChart) {
      this.xAxisToYaxisChart.clearChart();
    }
    // this.store.dispatch(setIntermediateData({ data: null }));
  }
}
